import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { RfidGroupId } from 'redux-store/slices/rfidGroups/types';
import type { RfidId } from 'redux-store/slices/ui/types';
import addRfidToRfidGroupThunk from 'redux-store/thunks/rfidGroups/addRfidToRfidGroupThunk';
import getAllRfidGroupsThunk from 'redux-store/thunks/rfidGroups/getAllRfidGroupsThunk';

import type { AddRfidsToGroupsModalStageProps } from '..';

interface CustomListItemButtonProps {
  index: number;
  length: number;
}

const CustomListItemButton = styled(ListItemButton)<CustomListItemButtonProps>(
  ({ theme, index, length }) => ({
    backgroundColor: theme.vars.palette.grey[100],
    padding: '10px 15px',
    borderRadius: `${index === 0 ? '16px 16px' : '0px 0px'} ${
      index === length - 1 ? '16px 16px' : '0px 0px'
    }`,
    borderBottom:
      index === length - 1
        ? 'none'
        : `1px solid ${theme.vars.palette.grey[300]}`,
  }),
);

const AddRfidsToGroups: FC<AddRfidsToGroupsModalStageProps> = ({
  state,
  setState,
  onClose,
  selectedRfids,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const rfidGroups = useAppSelector(status => status.rfidGroups.items);

  const [groupIDs, setGroupIDs] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectGroups = (id: number): void => {
    setGroupIDs(prevGroupIDs => {
      const newGroupIDs = new Set(prevGroupIDs);

      if (newGroupIDs.has(id)) {
        newGroupIDs.delete(id);
      } else {
        newGroupIDs.add(id);
      }

      return newGroupIDs;
    });
  };

  useEffect(() => {
    if (state.newGroupID !== undefined) {
      setGroupIDs(prevGroupIDs =>
        new Set(prevGroupIDs).add(state.newGroupID as number),
      );
    }
  }, [state.newGroupID]);

  const handleChangeRfidGroup = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Only add RFIDs to groups which they don't already belong to
      const rfidToExistingGroupsMap = new Map<RfidId, RfidGroupId[]>();
      rfidGroups.forEach(group => {
        group.rfids?.forEach(rfidId => {
          const existingGroups = rfidToExistingGroupsMap.get(rfidId) ?? [];
          rfidToExistingGroupsMap.set(rfidId, [...existingGroups, group.id]);
        });
      });

      const results = [];

      for (const targetGroupId of groupIDs) {
        try {
          const result = await dispatch(
            addRfidToRfidGroupThunk({
              rfidGroupId: targetGroupId,
              data: { rfids: selectedRfids },
            }),
          ).unwrap();

          if (!result.success) {
            results.push({
              groupId: targetGroupId,
              success: false,
              error: result.error || t('common:unknown_error'),
            });
          } else {
            results.push({
              groupId: targetGroupId,
              success: true,
            });
          }
        } catch (e) {
          results.push({
            groupId: targetGroupId,
            success: false,
            error: e instanceof Error ? e.message : t('common:unknown_error'),
          });
        }
      }

      const failedOperations = results.filter(result => !result.success);

      if (failedOperations.length > 0) {
        setError(
          t(
            'common:pages.rfid_groups.add_rfid_group.fail_to_add_rfid_to_group',
          ),
        );

        Sentry.captureMessage('Failed to add RFID to groups', {
          extra: {
            groupIDs: Array.from(groupIDs),
            failedOperations,
            affectedRfids: selectedRfids,
          },
        });
      }

      await dispatch(getAllRfidGroupsThunk({})).unwrap();

      if (failedOperations.length === 0) {
        setState({ name: '', rfids: [], stage: 'success' });
        setTimeout(() => {
          setState({ name: '', rfids: [], stage: 'name' });
          onClose();
        }, 1250);
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : t('common:unknown_error');
      setError(errorMessage);

      Sentry.captureException(e, {
        extra: {
          groupIDs: Array.from(groupIDs),
          affectedRfids: selectedRfids,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [rfidGroups, dispatch, selectedRfids, t, setState, groupIDs, onClose]);

  const selectedGroups = Array.from(groupIDs);

  return (
    <>
      <Box
        sx={{
          height: '45vh',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography variant="h2" mb={2} textAlign="center">
            {t('common:pages.rfid_groups.add_rfid_group.add_to_group_title')}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {t('common:pages.rfid_groups.add_rfid_group.add_to_group_desc')}
          </Typography>
        </Box>
        <Box mb={4} width="100%">
          <ListItemButton
            data-testid="new-rfid-group"
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderRadius: 2,
            }}
            onClick={() => setState(prev => ({ ...prev, stage: 'rfids' }))}
          >
            <ListItemText
              color="primary"
              primaryTypographyProps={{
                style: {
                  color: theme.vars.palette.primary.dark,
                  fontWeight: 500,
                },
              }}
              primary={t('common:pages.rfid_groups.add_rfid_group.title')}
            />
            <IconButton edge="end" disableRipple>
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          </ListItemButton>
        </Box>
        <Box>
          {rfidGroups.length > 0 ? (
            <Box>
              <Typography variant="h3" mb={1}>
                {t('common:pages.rfid_groups.add_rfid_group.your_rfid_groups')}
              </Typography>
              {rfidGroups.map((item, index, { length }) => {
                const { name, id } = item;

                return (
                  <ListItem disablePadding key={id}>
                    <CustomListItemButton
                      index={index}
                      length={length}
                      onClick={() => handleSelectGroups(id)}
                      data-testid="group-check-button"
                    >
                      <Box mr={2} alignItems="center" display="flex">
                        <CheckIcon
                          data-testid={`check-icon-${
                            selectedGroups.includes(id)
                              ? 'checked'
                              : 'unchecked'
                          }`}
                          sx={{
                            color: selectedGroups.includes(id)
                              ? 'primary.main'
                              : 'transparent',
                          }}
                        />
                      </Box>
                      <ListItemText data-testid="group-title" primary={name} />
                    </CustomListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          ) : null}
          <ErrorText error={error} />
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" mt={2} gap={1} width="100%">
        <Button
          onClick={onClose}
          color="neutral"
          fullWidth
          variant="flat"
          size="large"
          data-testid="cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleChangeRfidGroup}
          fullWidth
          variant="flat"
          disabled={selectedGroups.length === 0 || isLoading}
          size="large"
          data-testid="submit"
        >
          {t('common:pages.rfid_groups.add_rfid_group.add_to_group')}
        </Button>
      </Box>
    </>
  );
};

export default AddRfidsToGroups;
